import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { deleteJSON, getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import { action, computed, makeObservable, observable } from "mobx";
import { parseOrReturnError } from "./utils";
import { AccountUser, GetAccountUserResponse, GetAccountUsersResponse } from "@whatslly/types/GetAccountUsers";
import { GetAccountOrgsQuery, GetAccountOrgsResponse } from "@whatslly/types/GetAccountOrgs";
import { fromPromise } from "mobx-utils";
import { GetAccountResponse } from "@whatslly/types/GetAccount";
import { FiltersStore } from "./filtersStore";
import { SearchAccountsResponse } from "@whatslly/types/SearchAccounts";
import { UpdateAccountUserPermissionsBody, UpdateAccountUserPermissionsResponse } from "@whatslly/types/UpdateAccountUserPermissions";
import { InviteExternalUserToAccountBody, InviteExternalUserToAccountResponse } from "@whatslly/types/InviteExternalUserToAccount";
import { modalService } from "@whatslly/ui-components/modals";
import { CreateNewAccountModal } from "../components/Filters/components/AccountSelect/Modals/CreateNewAccountModal";
import { CreateAccountBody, CreateAccountResponse, UpdateAccountBody, UpdateAccountResponse } from "@whatslly/types/UpsertAccount";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { ChangeAccountNameModal } from "../components/Filters/components/AccountSelect/Modals/ChangeAccountNameModal";
import { AssignAccountOrgsBody, AssignAccountOrgsResponse } from "@whatslly/types/AssignAccountOrgs";
import { UnassignAccountOrgResponse } from "@whatslly/types/UnassignAccountOrg";
import { i18n } from "@whatslly/commons/i18nService";
import type { IOrganizationStore } from "./IOrganizationStore";
import { InjectOrganizationStore } from "./IOrganizationStore";
import { ResendTuvisInviteResponse } from "@whatslly/types/ResendTuvisInvite";
import { DeleteAccountUsersResponse } from "@whatslly/types/DeleteAccountUsers";

@injectable()
export class AccountStore {
  constructor(
    @inject(InjectUserStore) private userStore: IUserStore,
    @inject(FiltersStore) private readonly filtersStore: FiltersStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable<AccountStore, "_updatedAccountNamesMap">(this, {
      _updatedAccountNamesMap: observable,
      searchAccounts: action.bound,
      account: computed,
      accountId: computed,
      getAccount: action.bound,
      accountOrgs: computed,
      searchAccountUsers: action.bound,
      getAccountOrgs: action.bound,
      getAccountUsers: action.bound,
      getAccountUserById: action.bound,
      updateUserPermissions: action.bound,
      updateAccountName: action.bound,
    });
  }

  async searchAccounts(value: string) {
    const urlParams = new URLSearchParams({
      searchTerm: value ?? "",
    });

    const res = await getJSON({
      urlSuffix: `/integrations/accounts?${urlParams}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((r) => parseOrReturnError<SearchAccountsResponse>(r, this.logger));

    if (!res.success) {
      return { options: [] };
    }

    return {
      options: res.accounts.map((account) => {
        const label = this._updatedAccountNamesMap.has(account.id) ? this._updatedAccountNamesMap.get(account.id) : account.name;
        return { label, value: account.id };
      }),
    };
  }

  private _updatedAccountNamesMap = new Map<string, string>();

  get account() {
    const currentName = this._updatedAccountNamesMap.get(this.accountId);

    return fromPromise(
      this.getAccount(this.accountId).then((account) => {
        if (account && currentName) {
          return { ...account, name: currentName };
        }
        return account;
      })
    );
  }

  get accountId() {
    if (this.filtersStore.accountId) {
      return this.filtersStore.accountId;
    }
    if (this.userStore.accounts?.[0]) {
      return this.userStore.accounts?.[0].accountId;
    }
    return "";
  }

  async getAccount(accountId: string) {
    if (!accountId) {
      return null;
    }

    const res = await getJSON({
      urlSuffix: `/integrations/accounts/${accountId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((res) => res.json() as GetAccountResponse);

    if (!res.success) {
      return null;
    }

    return res.account;
  }

  async getAccountUserById(userId: string | undefined) {
    if (!userId) {
      return null;
    }

    const res = await getJSON({
      urlSuffix: `/integrations/accounts/${this.accountId}/users/${userId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((r) => parseOrReturnError<GetAccountUserResponse>(r, this.logger));
    if (!res.success || !res.user) {
      throw new Error("Failed to get account user");
    }
    return res.user;
  }

  async getAccountUsers(params: { searchTerm: string; hasNoAccountPermissions?: "true" | "false"; page?: string; sortBy?: string; sort?: string }) {
    const urlParams = new URLSearchParams(params);

    return await getJSON({
      urlSuffix: `/integrations/accounts/${this.accountId}/users?${urlParams}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((res) => res.json() as GetAccountUsersResponse);
  }

  async searchAccountUsers(value: string): Promise<AccountUser[]> {
    return await this.getAccountUsers({
      searchTerm: value,
      hasNoAccountPermissions: "true",
    }).then((res) => (res.success === true ? res.users : []));
  }

  get accountOrgs() {
    if (!this.accountId) {
      return fromPromise.resolve([]);
    }
    return fromPromise(this.getAccountOrgs(this.accountId).then((res) => (res.success ? res.orgs : [])));
  }

  async getAccountOrgs(accountId: string, query?: GetAccountOrgsQuery) {
    const params = new URLSearchParams();
    if (query?.sort) params.set("sort", query.sort);
    if (query?.sortBy) params.set("sortBy", query.sortBy);
    const queryString = params.toString();

    return await getJSON({
      urlSuffix: `/integrations/accounts/${accountId}/orgs${queryString ? `?${queryString}` : ""}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((res) => res.json() as GetAccountOrgsResponse);
  }

  async updateUserPermissions(userId: string, body: UpdateAccountUserPermissionsBody) {
    return await putJSON({
      urlSuffix: `/integrations/accounts/${this.accountId}/users/${userId}/permissions`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify(body),
    }).then((r) => parseOrReturnError<UpdateAccountUserPermissionsResponse>(r, this.logger));
  }

  async resendInviteExternalUser(users: AccountUser[]) {
    return await postJSON({
      urlSuffix: `/integrations/accounts/${this.accountId}/invite/resend`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({
        userIds: users.map((user) => user._id),
      }),
    }).then((r) => parseOrReturnError<ResendTuvisInviteResponse>(r, this.logger));
  }

  async deleteAccountUsers(userIds: string[]) {
    return await deleteJSON({
      urlSuffix: `/integrations/accounts/${this.accountId}/users`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({
        userIds,
      }),
    }).then((r) => parseOrReturnError<DeleteAccountUsersResponse>(r, this.logger));
  }

  async inviteExternalUserToAccount(body: InviteExternalUserToAccountBody) {
    return await putJSON({
      urlSuffix: `/integrations/accounts/${this.accountId}/invite`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify(body),
    }).then((r) => parseOrReturnError<InviteExternalUserToAccountResponse>(r, this.logger));
  }

  async createNewAccount() {
    if (!this.userStore.isAnyAdmin) return;
    const result = await modalService.openModal(CreateNewAccountModal).promise;
    if (!result) return;

    const payload: CreateAccountBody = {
      name: result.name,
    };

    const res: CreateAccountResponse = await postJSON({
      urlSuffix: "/integrations/accounts",
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify(payload),
    })
      .then((r) => parseOrReturnError<CreateAccountResponse>(r, this.logger))
      .catch((e) => {
        return { success: false, error: e.message };
      });

    if (!res.success) {
      modalService.openModal(Toast, {
        text: res.error ?? i18n.t("search_accounts.create_new_account_error"),
        type: "error",
      });
      return;
    }

    this.filtersStore.accountId = res.account.id;
    modalService.openModal(Toast, {
      text: i18n.t("search_accounts.create_new_account_success", { accountName: res.account.name }),
      type: "regular",
    });
  }

  async editAccountName() {
    const newName = await modalService.openModal(ChangeAccountNameModal, {
      accountName: await this.account.then((val) => val?.name || ""),
    }).promise;

    if (!newName) return;

    const res = await putJSON({
      urlSuffix: `/integrations/accounts/${this.accountId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ name: newName } as UpdateAccountBody),
    }).then((r) => parseOrReturnError<UpdateAccountResponse>(r, this.logger));

    if (!res.success) {
      modalService.openModal(Toast, {
        text: res.error ?? i18n.t("search_accounts.change_account_name_error"),
        type: "error",
      });
    }

    this.updateAccountName(this.accountId, newName);
    modalService.openModal(Toast, {
      text: i18n.t("search_accounts.change_account_name_success", {
        accountName: newName,
      }),
      type: "regular",
    });
  }

  updateAccountName(accountId: string, newName: string) {
    this._updatedAccountNamesMap.set(accountId, newName);
  }

  async unassignAccountOrg(orgId: string) {
    return await deleteJSON({
      urlSuffix: `/integrations/accounts/${this.accountId}/orgs/${orgId}`,
      etoken: this.userStore.etoken ?? "",
      body: JSON.stringify({}),
      pod: await this.orgStore.wPod,
    }).then((r) => parseOrReturnError<UnassignAccountOrgResponse>(r, this.logger));
  }

  async updateAccountOrgs(orgIds: string[]) {
    return await putJSON({
      urlSuffix: `/integrations/accounts/${this.accountId}/orgs`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ orgIds } as AssignAccountOrgsBody),
    })
      .then((r) => parseOrReturnError<AssignAccountOrgsResponse>(r, this.logger))
      .catch((e) => {
        return { success: false, error: e.message };
      });
  }
}
